import React, { useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BsTools,
  BsCalendar,
  BsPrinter,
  BsCalculator,
  BsArrowUpRight,
  BsListOl,
} from "react-icons/bs";
import { authFetch } from "../utils/authFetch";

// Helper to format Date objects to YYYY-MM-DD
const formatDateString = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function ScaderiMateriale() {
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadData = async () => {
    setBusy(true);
    setErr("");
    setData(null);
    setLoaded(false);

    try {
      const usp = new URLSearchParams();
      if (from) usp.set("from", formatDateString(from));
      if (to) usp.set("to", formatDateString(to));

      const res = await authFetch(`/rapoarte/scaderi-materiale?${usp.toString()}`);
      if (res && typeof res === "object" && Array.isArray(res.rows)) {
        setData(res);
        setLoaded(true);
      } else {
        setErr("Format răspuns invalid de la server.");
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcarea raportului de scăderi materiale.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Helper formats
  const fmtQty = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const fmtPrice = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  const fmtVal = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateRo = (val) => {
    if (!val) return "";
    const s = String(val);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("ro-RO");
  };

  // Calculate totals
  const totals = useMemo(() => {
    if (!data?.rows) return { count: 0, qty: 0, val: 0 };
    let qty = 0;
    let val = 0;

    data.rows.forEach((r) => {
      qty += Number(r.qty || 0);
      val += Number(r.value || 0);
    });

    return {
      count: data.rows.length,
      qty,
      val,
    };
  }, [data]);

  return (
    <div className="p-3">
      {/* Header card with gradient background */}
      <Card className="shadow-sm border-0 rounded-4 p-4 mb-4 bg-white d-print-none">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <BsTools className="text-warning" /> Scăderi Materiale pe Comenzi
            </h4>
            <p className="text-muted small mb-0">
              Vizualizează istoricul cronologic al pieselor și materialelor scăzute direct sau prin rețetă pe comenzile interne.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handlePrint}
              disabled={!loaded}
              className="rounded-3 fw-semibold d-flex align-items-center gap-1"
            >
              <BsPrinter /> Printează
            </Button>
            <Button
              variant="primary"
              onClick={loadData}
              disabled={busy}
              className="rounded-3 fw-semibold"
            >
              {busy ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Se încarcă...
                </>
              ) : (
                "Generează Raport"
              )}
            </Button>
          </div>
        </div>

        {/* Filter Form */}
        <Form className="bg-light p-3 rounded-4 border border-light-subtle">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold text-secondary small mb-1 d-flex align-items-center gap-1">
                <BsCalendar /> Perioadă
              </Form.Label>
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  selected={from}
                  onChange={(date) => setFrom(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="De la..."
                  className="form-control rounded-3 border-0 shadow-sm"
                />
                <span className="text-secondary">—</span>
                <DatePicker
                  selected={to}
                  onChange={(date) => setTo(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Până la..."
                  className="form-control rounded-3 border-0 shadow-sm"
                />
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {err && (
        <Alert variant="danger" className="rounded-3 mb-4 d-print-none">
          {err}
        </Alert>
      )}

      {/* Print View Header (only visible on print) */}
      <div className="d-none d-print-block mb-4">
        <h3 className="fw-bold text-center mb-1">SCĂDERI MATERIALE PE COMENZI</h3>
        <p className="text-center text-muted small">
          Perioada: {from ? formatDateRo(from) : "—"} — {to ? formatDateRo(to) : "—"}
        </p>
        <hr />
      </div>

      {loaded && data && (
        <>
          {/* KPI Metrics Cards */}
          <Row className="g-3 mb-4 d-print-none">
            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-secondary small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsListOl /> Număr Poziții
                </div>
                <h3 className="fw-bold mb-0 text-dark font-monospace">
                  {totals.count}
                </h3>
              </Card>
            </Col>

            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-success small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsArrowUpRight /> Total Cantitate Scăzută
                </div>
                <h3 className="fw-bold mb-0 text-success font-monospace">
                  {fmtQty(totals.qty)}
                </h3>
              </Card>
            </Col>

            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-primary small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsCalculator /> Valoare Totală (RON)
                </div>
                <h3 className="fw-bold mb-0 text-primary font-monospace">
                  {fmtVal(totals.val)}
                </h3>
              </Card>
            </Col>
          </Row>

          {/* Report Data Table */}
          <div className="table-responsive rounded-4 shadow-sm border border-light-subtle bg-white">
            <Table hover size="sm" className="align-middle mb-0 text-nowrap" style={{ fontSize: "0.85rem" }}>
              <thead className="bg-primary text-white">
                <tr className="bg-primary text-white align-middle text-center">
                  <th className="py-2 bg-primary text-white" style={{ width: "95px" }}>Dată Scădere</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "110px" }}>Nr. Comandă</th>
                  <th className="py-2 bg-primary text-white">Descriere Comandă</th>
                  <th className="py-2 bg-primary text-white">Client</th>
                  <th className="py-2 bg-primary text-white">Produs Fabricat</th>
                  <th className="py-2 bg-primary text-white">Piesă/Material Consumat</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "90px" }}>Cantitate</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "90px" }}>Preț (RON)</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "100px" }}>Valoare (RON)</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "90px" }}>Tip Scădere</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      Nu există scăderi de materiale înregistrate în perioada selectată.
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r, idx) => (
                    <tr key={r.id || idx}>
                      <td className="text-center">{formatDateRo(r.move_date)}</td>
                      <td className="text-center fw-bold">{r.order_no}</td>
                      <td className="text-truncate" style={{ maxWidth: "180px" }} title={r.order_descr || ""}>
                        {r.order_descr || "—"}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: "180px" }} title={r.customer_name || ""}>
                        {r.customer_name}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: "200px" }} title={`${r.prod_code} — ${r.prod_name}`}>
                        <span className="fw-semibold">{r.prod_code}</span> — {r.prod_name}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: "200px" }} title={`${r.mat_code} — ${r.mat_name}`}>
                        <span className="fw-semibold text-secondary">{r.mat_code}</span> — {r.mat_name}
                      </td>
                      <td className="text-end font-monospace fw-semibold">{fmtQty(r.qty)}</td>
                      <td className="text-end font-monospace">{fmtPrice(r.price)}</td>
                      <td className="text-end font-monospace fw-bold">{fmtVal(r.value)}</td>
                      <td className="text-center">
                        <span
                          className={`badge px-2 py-1 ${
                            r.rectype === 11
                              ? "bg-success-subtle text-success"
                              : "bg-warning-subtle text-warning-emphasis"
                          }`}
                        >
                          {r.type_label}
                        </span>
                      </td>
                    </tr>
                  ))
                )}

                {/* Final Total Summary Row */}
                {data.rows.length > 0 && (
                  <tr className="table-secondary fw-bold">
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-end">TOTAL:</td>
                    <td className="text-end font-monospace text-success">{fmtQty(totals.qty)}</td>
                    <td className="text-end">—</td>
                    <td className="text-end font-monospace text-primary">{fmtVal(totals.val)}</td>
                    <td className="text-center">—</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
